/**
 * Client-side dispatcher for the unified bot API.
 *
 * Response contract:
 *   {
 *     status: number,
 *     message: string,                   // combined human-readable summary
 *     results: Array<{
 *       domain: "form" | "formCondition" | "workflow" | "workflowCondition",
 *       action: "create" | "update" | "replace" | "generate" | "delete",
 *       data: object,                    // domain-specific payload
 *       nodeUpdates?: Array<object>,     // optional cross-cutting workflow node tweaks
 *       message?: string                 // per-result message
 *     }>
 *   }
 *
 * Apply semantics:
 *   - Results are applied in array order (later results may depend on earlier ones).
 *   - Best-effort: a failure on result[i] does NOT roll back result[i-1].
 *     Each result reports its own status; the caller surfaces partial-success in chat.
 *   - Handlers are registered by domain (one applier per domain). The applier
 *     receives `ctx.action` and branches internally (e.g. update vs replace vs delete).
 *   - Two skip modes:
 *       - `outOfScope`: domain not in `allowedDomains` (host page can't act on it).
 *         Surfaces a friendly bubble like "Workflow changes can't be made here."
 *       - `noHandler`: domain is allowed but no handler registered (config bug).
 *         Treated as a failure.
 *   - Out-of-scope skips do NOT count as failures — the rest of the batch can still
 *     report overall-ok.
 */

const DOMAIN_LABELS = {
  form: "form",
  formCondition: "form conditions",
  workflow: "workflow",
  workflowCondition: "workflow conditions",
};

/**
 * Build a dispatcher bound to a handler registry.
 *
 * @param {Object} config
 * @param {Object<string, (data, ctx) => Promise<{ok: boolean, message?: string}>>} config.handlers
 *   Map of domain -> async applier. The applier receives `(data, ctx)` where
 *   `ctx.action` ("create" | "update" | "replace" | "generate" | "delete") tells
 *   the applier what to do, and `ctx.nodeUpdates` carries any cross-cutting
 *   workflow-node tweaks. Returns `{ok, message?}`.
 *   Example keys: "form", "formCondition", "workflow", "workflowCondition".
 * @param {string[]} [config.allowedDomains]
 *   Domains this host page is allowed to mutate. Results outside this list are
 *   skipped with a user-friendly message instead of being applied. Omit to
 *   allow all domains (handlers still gate which actions actually run).
 */
export function createDispatcher({ handlers, allowedDomains } = {}) {
  const scope = allowedDomains ? new Set(allowedDomains) : null;

  return async function dispatch(response) {
    const results = Array.isArray(response?.results) ? response.results : [];
    const outcomes = [];

    for (let i = 0; i < results.length; i++) {
      const r = results[i];

      if (scope && !scope.has(r.domain)) {
        const label = DOMAIN_LABELS[r.domain] || r.domain;
        outcomes.push({
          index: i,
          domain: r.domain,
          action: r.action,
          ok: false,
          outOfScope: true,
          message: `${capitalize(label)} changes can't be made from this page.`,
        });
        continue;
      }

      const handler = handlers?.[r.domain];
      if (!handler) {
        outcomes.push({
          index: i,
          domain: r.domain,
          action: r.action,
          ok: false,
          noHandler: true,
          message: `No handler registered for domain "${r.domain}"`,
        });
        continue;
      }

      try {
        const ctx = { action: r.action, nodeUpdates: r.nodeUpdates, raw: r };
        const out = await handler(r.data, ctx);
        outcomes.push({
          index: i,
          domain: r.domain,
          action: r.action,
          ok: out?.ok !== false,
          skipped: out?.skipped === true,
          message: out?.message || r.message,
        });
      } catch (err) {
        outcomes.push({
          index: i,
          domain: r.domain,
          action: r.action,
          ok: false,
          message: err?.message || `Failed to apply ${r.domain}:${r.action}`,
        });
      }
    }

    const appliedCount = outcomes.filter(o => o.ok && !o.skipped).length;
    const okCount = outcomes.filter(o => o.ok).length;
    const failCount = outcomes.filter(o => !o.ok && !o.outOfScope).length;
    const outOfScopeCount = outcomes.filter(o => o.outOfScope).length;
    const skippedCount = outcomes.filter(o => o.ok && o.skipped).length;

    return {
      ok: failCount === 0 && appliedCount > 0,
      skipped: failCount === 0 && appliedCount === 0 && (skippedCount > 0 || outOfScopeCount > 0),
      outcomes,
      message: buildSummary(response?.message, outcomes),
      okCount,
      failCount,
      outOfScopeCount,
      skippedCount,
    };
  };
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function buildSummary(serverMessage, outcomes) {
  if (outcomes.length === 0) return serverMessage || "Nothing to apply.";

  const successes = outcomes.filter(o => o.ok).map(o => o.message).filter(Boolean);
  const failures  = outcomes.filter(o => !o.ok && !o.outOfScope).map(o => o.message).filter(Boolean);
  const skips     = outcomes.filter(o => o.outOfScope).map(o => o.message).filter(Boolean);

  if (failures.length === 0 && skips.length === 0) {
    return serverMessage || successes.join("; ");
  }

  const parts = [];
  if (successes.length) parts.push(`Applied: ${successes.join("; ")}`);
  if (failures.length)  parts.push(`Failed: ${failures.join("; ")}`);
  if (skips.length)     parts.push(skips.join(" "));
  return parts.join(". ");
}

export const DOMAIN = Object.freeze({
  FORM: "form",
  FORM_CONDITION: "formCondition",
  WORKFLOW: "workflow",
  WORKFLOW_CONDITION: "workflowCondition",
});

export const ACTION = Object.freeze({
  CREATE: "create",
  UPDATE: "update",
  REPLACE: "replace",
  GENERATE: "generate",
  DELETE: "delete",
});
